import { browserHistory } from 'react-router';
import bpClientLogger from './../common/helpers/bpClientLogger';
import moment from 'moment';
import get from 'lodash/get';
import mergeWith from 'lodash/mergeWith';

window.digitalData = {
  customer: {
    loggedInTime: {},
    client: {},
  },
  page: {
    error: [],
    pageName: '',
    visitorOriginPage: '',
    lengthOfArticle: '',
    tags: '',
  },
  version: {
    // 1.1 - Initial setup with customer, page information
    versionInfo: '1.1',
  },
  licensedMarkets: '',
  licensedStates: '',
  brokerType: '',
  clients: {
    accessedMarkets: '',
    accessedStates: '',
  },
  applications: {
    accessedMarkets: '',
    accessedStates: '',
  },
  quotes: {
    accessedStates: '',
  },
};

// Don't send the LOB AS-IS, there is a limit in strings in Adobe.
// Short LOB Maps for Analytics
const shortLobMap = {
  LARGEGROUP: 'LG',
  SMALLGROUP: 'SG',
  INDIVIDUAL: 'IND',
  SENIOR: 'MC',
};

// Update the customer info
export function setCustomerInfo(userProfile, isSimulation) {
  const PROFILE_OBJ = userProfile;
  const customerObj = {
    uuid: PROFILE_OBJ?.uuid,
    producerType: PROFILE_OBJ?.producer?.type,
    simulatedUser: isSimulation ? 'Yes' : 'No',
    encryptedTin: PROFILE_OBJ?.producer?.encryptedTaxId,
    accessLevel: PROFILE_OBJ?.accessLevel,
    userType: PROFILE_OBJ?.userType,
    markets: Object.keys(PROFILE_OBJ?.producer?.lineOfBusiness)
      .map(state => {
        const lobs = PROFILE_OBJ.producer.lineOfBusiness[state];
        const newLobs = lobs.map(lob => shortLobMap[lob.toUpperCase()]).join('-');

        return `${state}:${newLobs}`;
      })
      .join(','),
  };
  setForseeParameters(userProfile, isSimulation);
  mergeWith(window.digitalData.customer, customerObj);
}

export function setForseeParameters(userProfile, isSimulation) {
  const userId = userProfile.userId;
  const fsrUserExcluded = isSimulation ? true : false;
  let visitorType = userProfile.brokerRegDt
    ? moment(userProfile.brokerRegDt).isBefore('2019-01-01')
      ? 'RECURRENT'
      : 'NEW'
    : (visitorType = 'NA');

  try {
    if (userId && !fsrUserExcluded) {
      if (typeof fsReady === 'function') {
        fsReady(function () {
          FSR.CPPS.set('brokerID', userId);
          FSR.CPPS.set('visitorType', visitorType);
          FSR.CPPS.set('isExcluded', 'N');
        });
      }
    } else {
      if (typeof fsReady === 'function') {
        fsReady(function () {
          FSR.CPPS.set('isExcluded', 'Y');
        });
      }
    }
  } catch (err) {
    console.log('Error with fsready', err);
  }
}

export function setEnvironmentInfo(config) {
  window.digitalData.page.environment = config.environment;
}

export function setErrorInfo(compName, code, message) {
  window.digitalData.page.error.push({
    compName: compName,
    errorCode: code,
    errorMessage: message,
  });
}

export function setProductInfo(productInfo) {
  window.digitalData.customer.underwritingState = productInfo.state;
  window.digitalData.customer.lineOfBusiness = productInfo.lineofbusiness;
  window.digitalData.page.documentTitle = productInfo.title;
}
export function setClientInfo(rowSummary) {
  const CLIENT_OBJ = {
    clientID: rowSummary.clientID,
    writingAgentTin: rowSummary.writingAgentTin,
    paidTin: rowSummary.parentTin,
    marketSegment: rowSummary.marketSegment,
    subcriberCount: rowSummary.groupSize,
  };
  window.digitalData.customer.client = CLIENT_OBJ;
}
export function setLoginTime() {
  window.digitalData.customer.loggedInTime = new Date().toGMTString();
}

export function setLicensedMarkets(licensedMarkets) {
  window.digitalData.licensedMarkets = licensedMarkets.toString(', ');
}
export function setBrokerType(brokerType) {
  window.digitalData.brokerType = brokerType;
}
export function setProducerSpecialization(producerSpecializationTypes) {
  window.digitalData.producerSpecialization = producerSpecializationTypes;
}
export function setLicensedStates(licensedStates) {
  window.digitalData.licensedStates = licensedStates.toString(', ');
}

export function setMarketInfo(info) {
  const marketTypes = info.markets;
  const node = info.node;
  if (!window.digitalData[node].accessedMarkets.length) {
    window.digitalData[node].accessedMarkets = marketTypes;
  } else {
    let temp = window.digitalData[node].accessedMarkets.split(',');
    window.digitalData[node].accessedMarkets = [...new Set([...temp, ...marketTypes])];
  }

  window.digitalData[node].accessedMarkets = window.digitalData[node].accessedMarkets.toString(', ');
}

export function setStatesInfo(info) {
  const statesTypes = info.states;
  const node = info.node;
  if (!window.digitalData[node].accessedStates.length) {
    window.digitalData[node].accessedStates = statesTypes;
  } else {
    let temp = window.digitalData[node].accessedStates.split(',');
    window.digitalData[node].accessedStates = [...new Set([...temp, ...statesTypes])];
  }

  window.digitalData[node].accessedStates = window.digitalData[node].accessedStates.toString(', ');
}

export function setPageName(pageInfo) {
  window.digitalData.page.pageName = pageInfo?.pageName || 'Unknown';
}
export function setVisitorOriginPage(visitorOriginPage) {
  window.digitalData.page.visitorOriginPage = visitorOriginPage;
}
export function setLengthOfArticle(lengthOfArticle) {
  window.digitalData.page.lengthOfArticle = lengthOfArticle;
}
export function setTags(tags) {
  window.digitalData.page.tags = tags.toString(' , ');
}

export function setSearchPcp(tags) {
  window.digitalData.searchpcp = tags.toString(' , ');
}

export function setClientSearch(data) {
  window.digitalData.clientsearch = data;
}

// This middleware is to update window.digitalData with custom objects as per redux actions
const adobeDtmMiddleware = store => next => action => {
  try {
    switch (action.type) {
      case 'CLIENT_CONFIG':
        setEnvironmentInfo(action.payload.config.data);
        break;
      case 'HOME_CLIENT_CONFIG':
        setLoginTime();
        break;
      case 'HOME_SET_LOGGEDINUSER_INFO':
        action.payload.data && setCustomerInfo(action.payload.data.profile, false);
        break;
      case 'HOME_SET_SIMULATIONUSER_INFO':
        action.payload.data && setCustomerInfo(action.payload.data.profile, true);
        break;
      case 'CLICKED_ROW_SUMMARY':
        action.payload && setClientInfo(action.payload.rowSummary);
        break;
      case 'SWITCH_ASSIGNED_BROKER':
        action.payload.data && setCustomerInfo(action.payload.data.profile, true);
        break;
      case 'COMMISSIONS_PDF_FETCH_FAILED':
        let errorObj = get(action.payload, 'pdfFetchError.error');
        errorObj && setErrorInfo('ComSumPdf', errorObj.code, errorObj.message);
        break;
      case 'COMMISSIONS_XLS_FETCH_FAILED':
        errorObj = get(action.payload, 'xlsFetchError.error');
        errorObj && setErrorInfo('ComSumXls', errorObj.code, errorObj.message);
        break;
      case 'COMMISSIONS_SUMMARY_CURRENT_LIST_FETCH_FAILED':
        errorObj = get(action.payload, 'currentYearSummaryFetchError');
        errorObj && setErrorInfo('ComSumCurrList', errorObj.code, errorObj.message);
        break;
      case 'COMMISSIONS_SUMMARY_PREVIOUS_LIST_FETCH_FAILED':
        errorObj = get(action.payload, 'previousYearSummaryFetchError.summaryFetchError');
        errorObj && setErrorInfo('ComSumPrevList', errorObj.code, errorObj.message);
        break;
      case 'COMMISSIONS_SUMMARY_DASHBOARD_CURRENT_FETCH_FAILED':
        errorObj = get(action.payload, 'currentYearInsightsFetchError.error');
        errorObj && setErrorInfo('ComSumCurrDashboard', errorObj.code, errorObj.message);
        break;
      case 'COMMISSIONS_SUMMARY_DASHBOARD_PREVIOUS_FETCH_FAILED':
        errorObj = get(action.payload, 'previousYearInsightsFetchError.error');
        errorObj && setErrorInfo('ComSumPrevDashboard', errorObj.code, errorObj.message);
        break;
      case 'COMMISSION_DETAILS_FETCH_FAILED':
        errorObj = get(action.payload, 'commissions.error');
        errorObj && setErrorInfo('ComDetList', errorObj.code, errorObj.message);
        break;
      case 'COMMISSIONS_DETAILS_INSIGHT_FAILED':
        errorObj = get(action.payload, 'insightsFetchError.error');
        errorObj && setErrorInfo('ComDetDashboard', errorObj.code, errorObj.message);
        break;
      case 'USERAGENT_DETAILS_FETCH_FAILED':
        errorObj = get(action.payload, 'userAgentDetailsFetchError');
        errorObj && setErrorInfo('UserAgentList', errorObj.code, errorObj.message);
        break;
      case 'COMMISSION_PAGINATION_DETAILS_FETCH_FAILED':
        errorObj = get(action.payload, 'commissions.paginationError');
        errorObj && setErrorInfo('ComDetPagination', errorObj.code, errorObj.message);
        break;
      case 'AUTHORIZATION_ERROR':
      case 'HOME_AUTHORIZATION_ERROR':
        errorObj = get(action.payload, 'userProfile.error.error.error');
        errorObj && setErrorInfo('Login', errorObj.code, errorObj.message);
        break;
      case 'CLIENT_CONFIG_FETCH_ERROR':
      case 'HOME_CLIENT_CONFIG_FETCH_ERROR':
        errorObj = get(action.payload, 'config.error');
        errorObj && setErrorInfo('ConfigFetch', errorObj.code, errorObj.message);
        break;
      case 'ERRORMAP_CONFIG_FETCH_ERROR':
      case 'HOME_ERRORMAP_CONFIG_FETCH_ERROR':
        errorObj = get(action.payload, 'errorMap.error');
        errorObj && setErrorInfo('ErrorMapFetch', errorObj.code, errorObj.message);
        break;
      case 'PRODUCT_DOCUMENT_SELECTIONS':
        setProductInfo(action.payload.productDetails);
        break;
      case 'SET_LICENSED_MARKETS':
        setLicensedMarkets(action.payload);
        break;
      case 'SET_LICENSED_STATES':
        setLicensedStates(action.payload);
        break;
      case 'SET_MARKET_INFO':
        setMarketInfo(action.payload);
        break;
      case 'SET_STATES_INFO':
        setStatesInfo(action.payload);
        break;
      case 'SET_PAGE_NAME':
        setPageName(action.payload);
        break;
      case 'SET_VISITOR_ORIGIN_PAGE':
        setVisitorOriginPage(action.payload);
        break;
      case 'LENGTH_OF_ARTICLE':
        setLengthOfArticle(action.payload);
        break;
      case 'SET_TAGS':
        setTags(action.payload);
        break;
      case 'SET_BROKER_TYPE':
        setBrokerType(action.payload);
        break;
      case 'SET_PRODUCER_SPECIALIZATION':
        setProducerSpecialization(action.payload);
        break;
    }
  } catch (err) {
    // ignore the error or log the error to /logger
    bpClientLogger.warn(`Error popuplating digitalData for DTM. Type: ${action.type}. Reason: ${err.toString()}`);
  }

  return next(action);
};

export default adobeDtmMiddleware;
